import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AddUserFromACCDialog } from "@/components/AddUserFromACCDialog";
import { useToast } from "@/hooks/use-toast";
import { User } from "@shared/types";
import { Crown, UserPlus, Trash2, Shield, Users, UserCheck, Link, Copy, ExternalLink, Search, Building2, Check, X, Settings as SettingsIcon, AlertTriangle } from "lucide-react";
import CONFIG from "@/lib/config";
import { useParams } from "wouter";
import { apiRequest } from "@/lib/api";

export function Settings() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isAddUserOpen, setIsAddUserOpen] = useState(false);
  
  // Get current project ID from URL params
  const params = useParams<{ projectId?: string }>();
  const projectId = params?.projectId;
  
  
  // Tender settings state
  const [allowMultipleAwards, setAllowMultipleAwards] = useState(false);
  const [multipleAwardsWarning, setMultipleAwardsWarning] = useState("Warning: Multiple awards detected for this package.");
  
  const { data: users = [], isLoading } = useQuery<User[]>({
    queryKey: ['/api/users', projectId],
    queryFn: async () => {
      // Only fetch users if we have a projectId
      if (!projectId) {
        return [];
      }
      const url = new URL(`${CONFIG.API_BASE_URL}/api/users`);
      url.searchParams.set('projectId', projectId);
      const response = await fetch(url.toString(), {
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Failed to fetch users');
      return response.json();
    },
    enabled: !!projectId, // Only fetch when projectId is available
  });


  // Get project tender settings
  const { data: tenderSettings, isLoading: tenderSettingsLoading, refetch: refetchTenderSettings } = useQuery({
    queryKey: ["project-tender-settings", projectId],
    queryFn: async () => {
      if (!projectId) return null;
      return await apiRequest(`/api/project-tender-settings/${projectId}`);
    },
    enabled: !!projectId,
    onSuccess: (data) => {
      if (data) {
        console.log('Tender settings loaded:', data);
        setAllowMultipleAwards(data.allowMultipleAwards || false);
        setMultipleAwardsWarning(data.multipleAwardsWarning || "Warning: Multiple awards detected for this package.");
      }
    },
  });

  // App admin email (always you)
  const APP_ADMIN_EMAIL = "rongqichen@hgconstruction.co.uk";

  // Sync state with API data when it changes
  useEffect(() => {
    if (tenderSettings) {
      console.log('Syncing tender settings state:', tenderSettings);
      setAllowMultipleAwards(tenderSettings.allowMultipleAwards || false);
      setMultipleAwardsWarning(tenderSettings.multipleAwardsWarning || "Warning: Multiple awards detected for this package.");
      // Removal after submission is always allowed by default; no state to sync
    }
  }, [tenderSettings]);

  // Update tender settings mutation
  const updateTenderSettingsMutation = useMutation({
    mutationFn: async (settings: { allowMultipleAwards: boolean; multipleAwardsWarning: string }) => {
      if (!projectId) throw new Error('No project ID');
      return await apiRequest(`/api/project-tender-settings/${projectId}`, {
        method: 'PUT',
        body: JSON.stringify(settings),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["project-tender-settings", projectId] });
      toast({
        title: "Settings Updated",
        description: "Tender settings have been updated successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update tender settings.",
        variant: "destructive",
      });
    },
  });

  // Handle tender settings update
  const handleTenderSettingsUpdate = () => {
    updateTenderSettingsMutation.mutate({
      allowMultipleAwards,
      multipleAwardsWarning,
    });
  };



  const updatePermissionMutation = useMutation({
    mutationFn: async ({ userId, permissionLevel }: { userId: string; permissionLevel: string }) => {
      const response = await fetch(`${CONFIG.API_BASE_URL}/api/users/${userId}/permission`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ permissionLevel }),
      });
      if (!response.ok) throw new Error('Failed to update user');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/users'] });
      toast({
        title: "Success",
        description: "User permissions updated successfully.",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to update user permissions.",
        variant: "destructive",
      });
    },
  });

  const removeUserMutation = useMutation({
    mutationFn: async (userId: string) => {
      const response = await fetch(`${CONFIG.API_BASE_URL}/api/users/${userId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Failed to remove user');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/users'] });
      toast({
        title: "Success",
        description: "User removed successfully.",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to remove user.",
        variant: "destructive",
      });
    },
  });


  const handleUpdatePermission = (userId: string, permissionLevel: string) => {
    updatePermissionMutation.mutate({ userId, permissionLevel });
  };

  const handleRemoveUser = (userId: string, userEmail: string) => {
    // Prevent removing APP Admin
    if (userEmail === APP_ADMIN_EMAIL) {
      toast({
        title: "Error",
        description: "Cannot remove APP Admin.",
        variant: "destructive",
      });
      return;
    }
    
    removeUserMutation.mutate(userId);
  };

  const getPermissionBadgeColor = (permissionLevel: string) => {
    switch (permissionLevel) {
      case "app_admin":
        return "bg-purple-100 text-purple-800 border-purple-200";
      case "hub_super_user":
        return "bg-blue-100 text-blue-800 border-blue-200";
      case "hub_admin":
        return "bg-indigo-100 text-indigo-800 border-indigo-200";
      case "project_admin":
        return "bg-green-100 text-green-800 border-green-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const getPermissionIcon = (permissionLevel: string) => {
    switch (permissionLevel) {
      case "app_admin":
        return <Crown className="h-3 w-3" />;
      case "hub_super_user":
        return <Shield className="h-3 w-3" />;
      case "hub_admin":
        return <UserCheck className="h-3 w-3" />;
      case "project_admin":
        return <UserCheck className="h-3 w-3" />;
      default:
        return <Users className="h-3 w-3" />;
    }
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-48"></div>
          <div className="h-32 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
        <p className="text-gray-600 mt-2">Manage user permissions and system settings</p>
      </div>

      <Tabs defaultValue="users" className="w-full">
        <TabsList>
          <TabsTrigger value="users">User Management</TabsTrigger>
          <TabsTrigger value="tender-settings">Tender Settings</TabsTrigger>
        </TabsList>

        <TabsContent value="users">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>User Management</CardTitle>
                  <CardDescription>
                    Control who can access this procurement app and their permission levels
                  </CardDescription>
                </div>
                <Button 
                  className="bg-blue-600 hover:bg-blue-700 text-white shadow-md"
                  onClick={() => setIsAddUserOpen(true)}
                >
                  <UserPlus className="h-4 w-4 mr-2" />
                  Add User
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {!users || users.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p className="text-lg font-medium">No users added yet</p>
                    <p className="text-sm">Add your first user to get started</p>
                  </div>
                ) : (
                  users.map((user) => {
                    const isAppAdmin = user.email === APP_ADMIN_EMAIL;
                    return (
                      <div key={user.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg bg-white shadow-sm">
                        <div className="flex items-center gap-3">
                          {user.profileImageUrl ? (
                            <img
                              src={user.profileImageUrl}
                              alt="Profile"
                              className="h-12 w-12 rounded-full object-cover border-2 border-gray-200"
                            />
                          ) : (
                            <div className="h-12 w-12 rounded-full bg-gray-200 flex items-center justify-center">
                              <span className="text-lg font-medium text-gray-600">
                                {user.firstName ? user.firstName[0] : user.email[0].toUpperCase()}
                              </span>
                            </div>
                          )}
                          <div>
                            <div className="font-medium text-gray-900">
                              {user.firstName && user.lastName 
                                ? `${user.firstName} ${user.lastName}` 
                                : "Admin User"}
                              {isAppAdmin && (
                                <Crown className="inline h-4 w-4 ml-2 text-purple-600" />
                              )}
                            </div>
                            <div className="text-sm text-gray-500">{user.email}</div>
                            <div className="flex items-center gap-2 mt-2">
                              <Badge 
                                className={`${getPermissionBadgeColor(user.permissionLevel || "tender_user")} flex items-center gap-1`}
                              >
                                {getPermissionIcon(user.permissionLevel || "tender_user")}
                                {isAppAdmin ? "APP Admin" :
                                 user.permissionLevel === "hub_super_user" ? "Hub Super User" :
                                 user.permissionLevel === "hub_admin" ? "Hub Admin" :
                                 user.permissionLevel === "project_admin" ? "Project Admin" : "Tender User"}
                              </Badge>
                              <span className="text-xs text-gray-400">
                                Joined {new Date(user.createdAt).toLocaleDateString()}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {!isAppAdmin && (
                            <>
                              <Select 
                                value={user.permissionLevel || "tender_user"}
                                onValueChange={(value) => handleUpdatePermission(user.id, value)}
                                disabled={updatePermissionMutation.isPending}
                              >
                                <SelectTrigger className="w-40">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="z-[10010]">
                                  <SelectItem value="hub_super_user">Hub Super User</SelectItem>
                            <SelectItem value="hub_admin">Hub Admin</SelectItem>
                                  <SelectItem value="project_admin">Project Admin</SelectItem>
                                  <SelectItem value="tender_user">Tender User</SelectItem>
                                </SelectContent>
                              </Select>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleRemoveUser(user.id, user.email)}
                                disabled={removeUserMutation.isPending}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                          {isAppAdmin && (
                            <Badge className="bg-purple-100 text-purple-800 border-purple-200">
                              System Administrator
                            </Badge>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="tender-settings">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <SettingsIcon className="h-5 w-5" />
                Tender Settings
              </CardTitle>
              <CardDescription>
                Configure tender package award behavior and validation rules
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Multiple Awards Setting */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <Label htmlFor="allow-multiple-awards" className="text-base font-medium">
                      Allow Multiple Awards
                    </Label>
                    <p className="text-sm text-gray-600">
                      By default, each tender package can only have one award. Enable this to allow multiple awards per package.
                    </p>
                  </div>
                  <Switch
                    id="allow-multiple-awards"
                    checked={allowMultipleAwards}
                    onCheckedChange={setAllowMultipleAwards}
                  />
                </div>
                
                {allowMultipleAwards && (
                  <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5" />
                      <div className="space-y-2">
                        <p className="text-sm font-medium text-yellow-800">
                          Multiple Awards Warning
                        </p>
                        <p className="text-sm text-yellow-700">
                          When multiple awards are enabled, additional awards will display a warning message to users.
                        </p>
                        <div className="space-y-2">
                          <Label htmlFor="warning-message" className="text-sm font-medium text-yellow-800">
                            Warning Message:
                          </Label>
                          <Input
                            id="warning-message"
                            value={multipleAwardsWarning}
                            onChange={(e) => setMultipleAwardsWarning(e.target.value)}
                            placeholder="Enter warning message for multiple awards"
                            className="bg-white border-yellow-300 focus:border-yellow-500"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Removal after submission is now always allowed by default; toggle removed intentionally */}

              {/* Save Button */}
              <div className="flex justify-end pt-4 border-t">
                <Button
                  onClick={handleTenderSettingsUpdate}
                  disabled={updateTenderSettingsMutation.isPending}
                  className="min-w-[120px]"
                >
                  {updateTenderSettingsMutation.isPending ? "Saving..." : "Save Settings"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

      </Tabs>

      {/* Add User from ACC Dialog */}
      {projectId && (
        <AddUserFromACCDialog
          isOpen={isAddUserOpen}
          onClose={() => setIsAddUserOpen(false)}
          projectId={projectId}
          onUserAdded={() => {
            // Refresh the users list when users are added
            queryClient.invalidateQueries({ queryKey: ['/api/users'] });
          }}
          existingUsers={{
            hubAdmins: users.filter(user => 
              user.permissionLevel === 'hub_admin' || 
              user.permissionLevel === 'hub_super_user' ||
              user.permissionLevel === 'app_admin'
            ),
            projectUsers: users.filter(user => 
              user.permissionLevel === 'project_admin' || 
              user.permissionLevel === 'tender_user'
            )
          }}
        />
      )}
    </div>
  );
}

export default Settings;